import { Archive, Globe, GlobeLock, MoreHorizontal, Star, Trash } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Card, CardFooter, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Collection } from '../types/collection.types';
import { formatTimeAgo } from '@/lib/utils';
import { useCollections } from '@/features/collections/context/CollectionsContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Badge } from '@/shared/components/ui/badge';

const CollectionCard = ({ collection }: { collection: Collection }) => {
  const navigate = useNavigate();
  const { archiveCollection, unarchiveCollection, toggleProperty } = useCollections();

  const { letter, hue } = useMemo(
    () => getCollectionCardStyle(collection.title),
    [collection.title]
  );
  const isArchived = !!collection.archivedAt;
  const goToCollection = useCallback(() => {
    navigate(`/collection/${collection.id}`);
  }, [navigate, collection.id]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Card
          key={collection.id}
          onClick={goToCollection}
          className="group overflow-hidden cursor-pointer transition-all hover:shadow bg-background dark:bg-secondary blue:bg-secondary"
        >
          <div className="aspect-video overflow-hidden relative">
            <div
              className="w-full h-full flex items-center justify-center"
              style={{
                backgroundColor: `hsl(${String(hue)}, 70%, 95%)`,
                backgroundImage: `radial-gradient(circle at 70% 30%, hsl(${String(hue)}, 70%, 90%), hsl(${String(hue)}, 60%, 85%))`,
              }}
            >
              {!isArchived && (
                <div className="absolute top-2 right-2 flex-col space-y-2">
                  {collection.isFavourite && (
                    <div
                      title="Favourite collection"
                      onClick={e => {
                        e.stopPropagation();
                        void toggleProperty(collection.id, 'isFavourite');
                      }}
                      className="bg-yellow-500 p-1 rounded-sm"
                    >
                      <Star size={16} className=" text-white font-bold" />
                    </div>
                  )}
                  {collection.isPublic && (
                    <div
                      title="Collection is public"
                      onClick={e => {
                        e.stopPropagation();
                        void toggleProperty(collection.id, 'isPublic');
                      }}
                      className="bg-gray-400 p-1 rounded-sm"
                    >
                      <Globe size={16} className=" text-white font-bold" />
                    </div>
                  )}
                </div>
              )}

              {isArchived && (
                <Badge className="absolute top-2 right-2 bg-red-600 text-white">
                  <span className="text-xs">Archived</span>
                </Badge>
              )}
              <span
                className="text-6xl font-bold transform rotate-12 select-none transition-transform duration-500 group-hover:rotate-0"
                style={{ color: `hsl(${String(hue)}, 60%, 50%)` }}
              >
                {letter}
              </span>
            </div>
          </div>
          <CardHeader className="h-14 py-4 px-2 sm:px-4">
            <CardTitle className="truncate overflow-hidden">
              <span className="text-[16px] md:text-lg font-medium truncate overflow-hidden">
                {collection.title}
              </span>
            </CardTitle>
          </CardHeader>

          <CardFooter className="flex justify-between items-end border-t pt-4 px-2 sm:px-4">
            <span className="text-xs text-muted-foreground font-medium">
              {collection.archivedAt
                ? `Archived ${formatTimeAgo(collection.archivedAt)}`
                : `Updated ${formatTimeAgo(collection.updatedAt)}`}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <MoreHorizontal className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-secondary space-y-1">
                {!isArchived && (
                  <div className="space-y-1">
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation();
                        void toggleProperty(collection.id, 'isFavourite');
                      }}
                      className="cursor-pointer gap-2"
                    >
                      <Star size={16} />
                      {collection.isFavourite ? 'Unfavourite' : 'Favourite'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation();
                        void toggleProperty(collection.id, 'isPublic');
                      }}
                      className="cursor-pointer gap-2"
                    >
                      {collection.isPublic ? (
                        <GlobeLock className="h-3.5 w-3.5" />
                      ) : (
                        <Globe className="h-3.5 w-3.5" />
                      )}
                      {collection.isPublic ? 'Make private' : 'Make public'}
                    </DropdownMenuItem>
                  </div>
                )}
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    if (isArchived) {
                      void unarchiveCollection(collection);
                    } else {
                      void archiveCollection(collection);
                    }
                  }}
                  className="cursor-pointer gap-2"
                >
                  {isArchived ? (
                    <Archive className="h-3.5 w-3.5" />
                  ) : (
                    <Trash className="h-3.5 w-3.5" />
                  )}
                  {isArchived ? 'Unarchive' : 'Delete'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardFooter>
        </Card>
      </TooltipTrigger>
      <TooltipContent>
        <span>{collection.title}</span>
      </TooltipContent>
    </Tooltip>
  );
};

export default CollectionCard;

function getCollectionCardStyle(title: string) {
  // Find the first alphanumeric character
  const alphanumericMatch = title.match(/[a-zA-Z0-9]/);

  // Default to 'C' if no alphanumeric characters found
  const firstLetter = alphanumericMatch ? alphanumericMatch[0].toUpperCase() : 'C';

  // Calculate hue based on character code
  // For letters A-Z: 65-90, numbers 0-9: 48-57
  const charCode = firstLetter.charCodeAt(0);

  // Apply different formulas for letters vs numbers to get good color distribution
  const hue = /[A-Z]/.test(firstLetter)
    ? ((charCode - 65) * 13) % 360 // For letters
    : ((charCode - 48) * 36) % 360; // For numbers (more spread out)

  return { letter: firstLetter, hue };
}
